import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableMoney, EditableText } from '../../components/Editable';
import { count, money } from '../../lib/format';
import { channel, pricedLine, productLabel, tacticDeliveryMetrics } from '../../lib/plan';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { Frame, StatTile } from './primitives';
import { kpiLabelForGoal } from './kpi';

/**
 * tactic_measurement (design.md V12; brief slide map: "FULL · light
 * hero-scale stat tiles (stats-style)"). Exactly the delivery data v1's
 * `TacticMeasurementPage` reads (`tacticDeliveryMetrics` — the same bound
 * metric logic `ProductPlanPage` uses), grown to hero scale
 * (`mv3-stat-tile-hero`), plus the V15 `estConversionsMonthly` line when the
 * pricing engine has produced one.
 */
export function TacticMeasurementPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const productId = section.productId;
  const { productName } = useContentLibrary();
  const label = productId ? (productName(productId) ?? productLabel(proposal, productId)) : section.title;
  const line = productId ? channel(proposal, productId) : null;
  const metrics = productId ? tacticDeliveryMetrics(proposal, productId) : null;
  const priced = productId ? pricedLine(proposal, productId) : null;
  const conversions = priced?.estConversionsMonthly ?? null;
  const kpiLabel = kpiLabelForGoal(proposal.objective.primaryGoal);

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-measure-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-measure-body">
          <div className="mv3-eyebrow">{label} / Measurement</div>
          <h1 className="mv3-h1 mv3-measure-name">{label}</h1>
          <div className="mv3-measure-copy">
            <EditableText
              value={section.content.body}
              field="body"
              placeholder="Add a short delivery note"
              readOnly={readOnly}
              clampLines={3}
              wordBudget={30}
              onSave={(body) => actions.saveContent(section, { body })}
            />
          </div>
          {productId && line && metrics ? (
            <div className="mv3-measure-tiles">
              <StatTile
                className="mv3-stat-tile-hero"
                label="Budget"
                value={
                  <EditableMoney
                    value={line.monthlyBudget}
                    display={money(line.monthlyBudget)}
                    readOnly={readOnly}
                    label={`${label} monthly budget`}
                    onSave={(next) => actions.setChannelBudget(productId, next, label)}
                  />
                }
              />
              {metrics.kind === 'cpm' ? (
                <StatTile className="mv3-stat-tile-hero" label="Est. impressions" value={count(metrics.impressions)} />
              ) : null}
              {metrics.kind === 'cpc' ? (
                <StatTile className="mv3-stat-tile-hero" label="Est. clicks" value={count(metrics.clicks)} />
              ) : null}
              {metrics.kind === 'flat' ? (
                <StatTile
                  className="mv3-stat-tile-hero"
                  label={metrics.unitLabel ? `Number of ${metrics.unitLabel}` : 'Units'}
                  value={count(metrics.units)}
                />
              ) : null}
              <StatTile
                className="mv3-stat-tile-hero"
                label={metrics.kind === 'flat' ? 'Drop date' : 'Dates'}
                value={metrics.dateLabel}
              />
              {conversions !== null ? (
                <StatTile
                  className="mv3-stat-tile-hero"
                  label={`Est. ${kpiLabel.toLowerCase()} / mo`}
                  value={count(conversions)}
                />
              ) : null}
            </div>
          ) : (
            <p className="note">This channel is not in the current plan.</p>
          )}
        </div>
      </Frame>
    </PageShell>
  );
}
